<template>
  <!-- [sc-46314] Maschera con un buco: il buco e' l'elemento da guardare.
       `box-shadow` enorme invece di quattro rettangoli, cosi' la parte scura
       e' un solo elemento e non ci sono fessure agli angoli. -->
  <div v-if="riflettore && buco" class="onb-spot" @click="avanti">
    <div class="onb-spot__buco" :style="stileBuco"></div>
    <div class="onb-spot__fumetto" :style="stileFumetto" aria-live="polite">
      <p v-for="(frase, i) in frasi" :key="i" class="onb-spot__testo">
        {{ frase }}
      </p>
      <div class="onb-spot__comandi">
        <button type="button" class="onb-spot__salta" @click.stop="chiudi">
          Chiudi
        </button>
        <span class="onb-spot__spazio"></span>
        <!-- [sc-46314, review 48629] Si torna indietro. Il giro del
             preventivo e' di quindici passi: senza, chi si distrae per un
             secondo deve rifarlo da capo o rinunciare. -->
        <button
          v-if="passo > 0"
          type="button"
          class="onb-spot__indietro"
          @click.stop="indietro"
        >
          Indietro
        </button>
        <button type="button" class="onb-spot__avanti" @click.stop="avanti">
          {{ ultimo ? "Ho capito" : "Avanti" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { inFrasi, riflettorePerRotta } from "@/utilities/onboarding";

/** Quanto respiro lasciare intorno all'elemento illuminato. */
const MARGINE = 6;

/** Quanto vicino al bordo dello schermo si considera «fuori vista». */
const BORDO = 80;

export default {
  name: "OnboardingSpotlight",
  data() {
    return { passo: 0, buco: null };
  },
  computed: {
    riflettore() {
      const user = this.$store.state.currentUser;
      if (!user) return null;
      return riflettorePerRotta({
        stato: this.$store.getters["onboarding/stato"],
        user,
        routeName: this.$route.name,
        configured: Boolean(
          this.$store.getters["company/branding"]?.configured
        ),
        readOnly: Boolean(this.$store.getters["company/readOnly"]),
      });
    },
    passoCorrente() {
      return this.riflettore?.passi[this.passo] ?? { testo: "" };
    },
    ultimo() {
      return this.passo === (this.riflettore?.passi.length ?? 1) - 1;
    },
    frasi() {
      return inFrasi(this.passoCorrente.testo);
    },
    stileBuco() {
      if (!this.buco) return {};
      return {
        top: this.buco.top - MARGINE + "px",
        left: this.buco.left - MARGINE + "px",
        width: this.buco.width + MARGINE * 2 + "px",
        height: this.buco.height + MARGINE * 2 + "px",
      };
    },
    stileFumetto() {
      if (!this.buco) return {};
      // Sotto l'elemento se c'e' spazio, sopra altrimenti: un fumetto che
      // esce dallo schermo lascia il cliente con un buco nero e nessuna
      // spiegazione.
      const sotto = window.innerHeight - (this.buco.top + this.buco.height);
      const inGiu = sotto > 160;
      return inGiu
        ? { top: this.buco.top + this.buco.height + 14 + "px" }
        : { bottom: window.innerHeight - this.buco.top + 14 + "px" };
    },
  },
  watch: {
    /**
     * Un riflettore che ricompare riparte dal primo passo.
     *
     * [sc-46314] Il componente resta montato anche dopo la fine del giro,
     * con `passo` fermo sull'ultimo indice: senza questo, «Spiegami questa
     * pagina» rimostrerebbe solo la fine del giro. Si azzera solo quando
     * prima non c'era, altrimenti ogni riposizionamento riporterebbe al
     * primo passo.
     */
    riflettore(adesso, prima) {
      if (!adesso || prima) return;
      this.passo = 0;
      this.$nextTick(this.posizionaBuco);
    },
  },
  mounted() {
    this.posizionaBuco();
    window.addEventListener("resize", this.posizionaBuco);
    window.addEventListener("scroll", this.posizionaBuco, true);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.posizionaBuco);
    window.removeEventListener("scroll", this.posizionaBuco, true);
  },
  methods: {
    /**
     * Trova l'elemento del passo corrente e ci mette sopra il buco.
     *
     * Se non lo trova passa al successivo, e se non ne trova nessuno chiude
     * tutto: il markup di queste schede e' cambiato piu' volte, e un buco
     * nero sul posto sbagliato e' peggio di niente.
     */
    posizionaBuco() {
      const passi = this.riflettore?.passi ?? [];
      const documento = this.$el?.ownerDocument ?? document;
      for (let i = this.passo; i < passi.length; i++) {
        const elemento = documento.querySelector(
          `[data-tour="${passi[i].ancora}"]`
        );
        if (!elemento) continue;
        this.portaInVista(elemento);
        const r = elemento.getBoundingClientRect();
        this.passo = i;
        this.buco = {
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
        };
        return;
      }
      this.buco = null;
      this.chiudi();
    },
    /**
     * Porta l'elemento in mezzo allo schermo, se non ci si vede.
     *
     * I moduli di queste tre schede sono lunghi: illuminare il pulsante che
     * salva mentre il cliente sta guardando la prima riga vuol dire non
     * illuminare niente. Si sposta solo quando serve, perché una pagina che
     * salta a ogni passo sembra rotta.
     */
    portaInVista(elemento) {
      if (typeof elemento.scrollIntoView !== "function") return;
      const r = elemento.getBoundingClientRect();
      const fondo = typeof r.bottom === "number" ? r.bottom : r.top + r.height;
      const altezza = window.innerHeight || 0;
      if (r.top < BORDO || fondo > altezza - BORDO) {
        elemento.scrollIntoView({ block: "center" });
      }
    },

    avanti() {
      const passi = this.riflettore?.passi ?? [];
      if (this.passo >= passi.length - 1) return this.chiudi();
      this.passo += 1;
      return this.posizionaBuco();
    },
    /**
     * Un passo indietro.
     *
     * [sc-46314, review 48629] `posizionaBuco` cerca in avanti a partire da
     * `passo`: se l'ancora del passo precedente non c'e' piu' - un campo che
     * compare solo in certi casi - si torna dove si era, invece di finire
     * su un buco vuoto.
     */
    indietro() {
      if (this.passo === 0) return undefined;
      this.passo -= 1;
      return this.posizionaBuco();
    },
    chiudi() {
      const chiave = this.riflettore?.chiave;
      if (!chiave) return undefined;
      return this.$store.dispatch("segnaPassoTutorial", chiave);
    },
  },
};
</script>

<style scoped>
.onb-spot {
  position: fixed;
  inset: 0;
  z-index: 1100;
}

.onb-spot__buco {
  position: fixed;
  border-radius: 8px;
  box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.55);
  pointer-events: none;
}

.onb-spot__fumetto {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  width: min(320px, calc(100vw - 32px));
  background: var(--page-bg, #fff);
  border-radius: 10px;
  padding: 14px 16px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.3);
}

.onb-spot__testo {
  margin: 0 0 6px;
  line-height: 1.45;
}

.onb-spot__testo:last-of-type {
  margin-bottom: 12px;
}

.onb-spot__comandi {
  display: flex;
  align-items: center;
  gap: 8px;
}

.onb-spot__spazio {
  flex: 1;
}

.onb-spot__indietro {
  border: 0.5px solid #cbd5e1;
  border-radius: 8px;
  padding: 6px 12px;
  background: transparent;
  cursor: pointer;
}

.onb-spot__salta {
  border: 0;
  background: none;
  color: #64748b;
  text-decoration: underline;
  cursor: pointer;
}

.onb-spot__avanti {
  border: 0;
  border-radius: 8px;
  padding: 7px 14px;
  background: var(--brand);
  color: var(--brand-ink, #fff);
  cursor: pointer;
}
</style>
